<template>

    <v-row justify="center" no-gutters class="px-lg-15">
        <Alert :key="NaN" v-if="deleteSelectedAudioElementDialog" :dialogVal="true" type="dialog" title="Are you sure you want to delete this Audio Element?" text="Your Audio Element will be permanently deleted. This action cannot be undone." @dialogClicked="deleteAudioElement"/>
        <v-col cols="12" xl="7" lg="8" md="12" sm="12" xs="12" class="pr-lg-5">
            <v-row v-if="viewLoading || !letUserSeePost" justify="center" no-gutters>
                <v-col cols="12">
                    <v-skeleton-loader height="600" type="image" class="hidden-md-and-down"></v-skeleton-loader>
                    <v-skeleton-loader height="450" type="image" class="hidden-lg-and-up hidden-xs-only"></v-skeleton-loader>
                    <v-skeleton-loader height="200" type="image" class="hidden-sm-and-up"></v-skeleton-loader>
                </v-col>
            </v-row>

            <v-row v-else justify="center">
                <v-col cols="12" xl="10" lg="10" md="10" sm="10">
                    <TextIndexCardContainer
                        v-if="currentAudioElement != null"
                        ref="textIndexCardContainer"
                        :textAddedToDisplay="textAddedToDisplay"
                        :textPost="postData"
                        :audioData="currentAudioElement"
                        :audioIsLoading="textCardAudioIsLoading"
                        :audioIsPlaying="textCardAudioIsPlaying"
                        :changeAudioElementClicked="changeAudioElementClicked"
                        :changeAudioSegmentClicked="changeAudioSegmentClicked"
                        @playOrPauseAudioClicked="playOrPauseAudio"
                        @loadAndPreloadAudioData="loadAndPreloadAudioData"
                        @captionVolumeChanged="adjustCaptionVolume"
                        @playbackRateChanged="adjustPlaybackRate"
                        @resetChangeAudioElementClicked="changeAudioElementClicked = false"
                        @resetChangeAudioSegmentClicked="changeAudioSegmentClicked = null"
                        @update-selected-text-added-language="updateSelectedTextAddedLanguage"
                    />
                </v-col>
            </v-row>

            <!-- Show More/Less Button to view description -->
            <div v-if="letUserSeePost" style="position: relative; max-width: 100%;" class="full-width">
                <v-row justify="space-around" align="center" no-gutters class="mt-5">
                    <v-col cols="10" :class="viewFullDescription ? '' : 'd-inline-block text-truncate'">
                        <span v-if="!editPost" class="text-h5 text-xl-h4 text-lg-h4 font-weight-bold">{{postData.title}}</span>
                        <v-text-field v-else ref="editTitle" v-model="postData.title" @change="postMetadataHasChanged = true" :rules="titleRules" :counter="50" outlined class="text-subtitle-1 text-xl-h5 text-lg-h5 font-weight-bold"></v-text-field>
                    </v-col>

                    <v-col cols="2" align="end">
                        <v-menu v-model="menu" rounded="lg" offset-y bottom origin="center" transition="scale-transition" close-on-content-click>
                            <template v-slot:activator="{ on }">
                                <v-btn v-on="on" @click.stop="menu = !menu" icon :disabled="editPost">
                                    <v-icon :color="$vuetify.theme.dark ? 'amber' : 'primary'">mdi-dots-vertical</v-icon>
                                </v-btn>
                            </template>
                            <v-list rounded>
                                <v-list-item @click.stop="showShareDialog = true, menu = !menu">
                                    <v-icon class="pr-2">mdi-share</v-icon>Share<br>
                                </v-list-item>
                                <v-list-item @click.stop="reportDialog = true, menu = !menu">
                                    <v-icon class="pr-2">mdi-flag</v-icon>Report
                                </v-list-item>
                                <v-list-item v-if="postData.textAddedLanguage" @click.stop="textCardTranslationDialog = true, menu = !menu">
                                    <v-icon class="pr-2">mdi-translate-variant</v-icon>Translate Text Cards<br/>
                                </v-list-item>
                                <v-list-item v-if="isUsersPost && postData.thumbnail != '' && postData.thumbnail != null" @click.stop="triggerFileUpload(), menu = !menu"> 
                                    <v-icon class="pr-2">mdi-image-plus</v-icon>Change Thumbnail<br>
                                </v-list-item>
                                <v-list-item v-if="isUsersPost && (postData.thumbnail === '' || postData.thumbnail === null)" @click.stop="triggerFileUpload(), menu = !menu"> 
                                    <v-icon class="pr-2">mdi-image-plus</v-icon>Add Post Thumbnail<br>
                                </v-list-item>
                                <v-list-item v-if="isUsersPost" @click.stop="editPost = true, viewFullDescription = true, menu = !menu"> 
                                    <v-icon class="pr-2">mdi-pencil</v-icon>Edit Title & Description<br>
                                </v-list-item>
                                <!-- <v-list-item v-if="isUsersPost" @click.stop="$store.dispatch('downloadVttFile', {post: postData, audio: audioData}), menu = !menu"><v-icon class="pr-2">mdi-file-download</v-icon>Download VTT</v-list-item> -->
                                <v-list-item v-if="isUsersPost" @click.stop="updateAnonymousMode(), menu = !menu">
                                    <v-icon class="pr-2">{{postData?.anonymousMode ? 'mdi-incognito-circle' : 'mdi-incognito-circle-off'}}</v-icon>Anonymous Mode: {{postData?.anonymousMode ? "ON" : "OFF"}}<br>
                                </v-list-item>
                            </v-list>
                        </v-menu>
                        <Report v-if="reportDialog" :dialog="reportDialog" page="viewPost" :post="postData" @close-report="reportDialog = false"/>
                        <UploadPhotoDialog v-if="uploadPhotoDialog" :userId="userData.userId" :dialog="uploadPhotoDialog" :postType="postData.postType" :postId="postData.postId" :existingThumbnail="(postData.thumbnail != '' && postData.thumbnail != null) " @close-dialog="uploadPhotoDialog = false" @thumbnail-upload-success="thumbnailUploadSuccess"/>
                        <ShareDialog v-if="showShareDialog" :dialog="showShareDialog" :postData="postData" @close-dialog="showShareDialog = false"/>
                        <TextCardTranslation v-if="textCardTranslationDialog" :dialog="textCardTranslationDialog" page="viewTextPost" :textPost="postData" @close-text-card-translation="closeTextCardTranslationDialog"/>
                    </v-col>
                </v-row>
                <div v-if="viewFullDescription" style="position: relative; max-width: 100%;" class="full-width mb-2">
                    <v-row v-if="letUserSeePost" justify="start" align="center" no-gutters class="my-3">
                        <router-link v-if="postData.userId === currentUserId" :to="{ path: 'profile' }" class="text-decoration-none accent--text">
                            <p class="subtitle-2 font-weight-light font-italic mb-0 py-0 accent--text"> @{{postData.username ? postData.username : 'Unknown' }}</p>
                        </router-link>
                        <router-link v-else :to="{name: 'view-user-profile', query: {id: postData.userId}}" class="text-decoration-none accent-text">
                            <p class="subtitle-2 font-weight-light font-italic mb-0 py-0 accent--text"> @{{postData.username ? postData.username : 'Unknown' }}</p>
                        </router-link>
                        <span class="px-2">|</span>
                        <p class="subtitle-2 font-weight-light mb-0 py-0">{{postData.postDate}}</p>
                    </v-row>

                    <v-row v-if="letUserSeePost" justify="start" align="center" no-gutters>
                        <span v-if="!editPost" style="max-width: 100%; overflow-x: hidden;" class="subtitle-1 text--secondary font-weight-light">{{postData.description}}</span>
                        <v-textarea v-else ref="editDescription" v-model="postData.description" outlined style="max-width: 100%; overflow-x: hidden;" class="subtitle-1 font-weight-light" :counter="750" :rules="descriptionRules" @update:error="postMetadataError = true" @change="postMetadataHasChanged = true"></v-textarea>
                    </v-row>

                    <v-row v-if="isMobile" justify="end" align="center" no-gutters class="mb-5 mt-2 px-5">
                        <v-col cols="12" xl="7" lg="11" md="3" sm="4" align="center">
                            <v-autocomplete v-if="filter.languageCodes.length > 1 && !audioLoading" v-model="filter.languageSelected" :items="filter.languageCodes" label="Filter by language" append-icon="mdi-filter-menu" hide-details class="children-cursor--pointer"/>
                            <v-skeleton-loader v-else type="text@2" class="pa-2"></v-skeleton-loader>
                        </v-col>
                    </v-row>
                </div>
                <div v-if="!viewFullDescription && !editPost" v-bind:class="[$vuetify.theme.dark ? 'faded-dark' : 'faded']"></div>
                <div v-if="!editPost" style="width: 100%;" class="d-flex justify-space-around align-center">
                    <v-divider/>
                    <v-btn text x-small :ripple="false" color="primary" @click="viewFullDescription = !viewFullDescription">
                        <v-icon color="primary">{{ viewFullDescription ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                        {{ viewFullDescription ? 'View Less' : 'View More'}}
                        <v-icon color="primary">{{ viewFullDescription ? 'mdi-chevron-up' : 'mdi-chevron-down'}}</v-icon>
                    </v-btn>
                    <v-divider/>
                </div>
                <v-row v-if="editPost && letUserSeePost" justify="center" no-gutters>
                    <v-btn text color="primary" :disabled="editPostMetadataLoading" :loading="editPostMetadataLoading" @click.stop="editPostMetadata">save</v-btn>
                    <v-btn text color="red" :disabled="editPostMetadataLoading" @click.stop="editPost = false">cancel</v-btn>
                </v-row>
            </div>
        </v-col>


        <v-col cols="12" xl="3" lg="4" md="12" sm="12" :style="{maxHeight: getMaxHeightMainContainer}" style="max-height: 100vh; overflow-y: hidden; overflow-x: hidden;" class="bordered mt-5 mt-xl-0 mt-lg-0">

            <!-- we want to see if there are more than 2 in the array because we start with one ('all'), and only want to filter if there are more than 1 language in the audiodata. -->
            <v-row justify="end" align="center" no-gutters class="mx-0 mt-2 px-5">
                <v-col v-if="!isMobile" cols="12" xl="7" lg="11" md="3" sm="4" align="center">
                    <v-autocomplete v-if="filter.languageCodes.length > 1 && !audioLoading" v-model="filter.languageSelected" :items="filter.languageCodes" label="Filter by language" append-icon="mdi-filter-menu" hide-details class="children-cursor--pointer"/>
                    <v-skeleton-loader v-else type="text@2" class="pa-2"></v-skeleton-loader>
                </v-col>
            </v-row>
            <v-row justify="center" align="center" class="mx-0 mb-2 px-5">
                <v-divider/>
                <v-tooltip top>
                    <template v-slot:activator="{ on }">
                        <v-btn v-on="on" @click.stop="addNewAudio()" depressed color="primary" :disabled="audioLoading" class="hidden-sm-and-down">Contribute
                            <v-icon right>mdi-plus</v-icon>
                        </v-btn>
                        <v-btn @click.stop="addNewAudio()" depressed color="primary" :disabled="audioLoading" style="max-width: 60%;" class="hidden-md-and-up">Contribute
                            <v-icon right>mdi-plus</v-icon>
                        </v-btn>
                    </template>
                    <span>Add your own audio</span>
                </v-tooltip>
                <v-divider/>
            </v-row>

            <v-row :style="{maxHeight: getMaxHeightSubContainer}" style="max-height: 100vh; overflow-y: scroll;" class="bordered mx-0 px-5">
                <v-row v-if="audioLoading" class="my-0 py-0 hidden-lg-and-up hidden-xs-only">
                    <v-col v-for="i in 3" :key="i" cols="6" class="my-0 pt-0 px-2 pb-5 hidden-lg-and-up hidden-xs-only">
                        <v-skeleton-loader height="100" type="image" class="rounded-lg"></v-skeleton-loader>
                    </v-col>
                    <v-col v-for="i in 3" :key="i+3" cols="6" class="my-0 pt-0 px-2 pb-5 hidden-lg-and-up hidden-xs-only">
                        <v-skeleton-loader height="100" type="image" class="rounded-lg"></v-skeleton-loader>
                    </v-col>
                </v-row>

                <v-col v-if="leftAudioData.length > 0" cols="12" xs="12" sm="6" md="6" lg="12" xl="12" class="hidden-xs-only hidden-lg-and-up ma-0 pl-0 py-0 justify-end">
                    <v-container fluid v-for="audio in leftAudioData" :key="audio.audioDataId" class="pt-0 px-0 pb-5">
                        <AudioElement
                            v-if="audio.audioData.audio.length > 0 && !audioLoading"
                            :audioObject="audio"
                            :postAudioDataId="postData.audioDataId"
                            :postOwner="postData.userId"
                            :userData="userData"
                            :selectedAudioId="selectedAudioId"
                            :isGroupAdmin="isGroupAdmin"
                            :postType="postType"
                            :postData="postData"
                            :videoLoading="false"
                            :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                            :anonymousMode="postData?.anonymousMode || false"
                            :isTextPost="postData.mediaType === 'text' ? true : false"
                            @delete-selected-audio-element="deleteDialog"
                            @audioChanged="changeAudioElement"
                            @changeAudioSegment="changeAudioSegment"
                            @edit-caption="editCaption"
                            @saveEditAudioElementTitle="saveEditAudioElementTitle"
                            @update-specific-audio-data="getSpecificAudioData"
                            @update-selected-subtitle-language="updateSelectedSubtitleLanguage"
                        />
                    </v-container>
                </v-col>

                <v-col v-if="rightAudioData.length > 0" cols="12" xs="12" sm="6" md="6" lg="12" xl="12" class="hidden-xs-only hidden-lg-and-up ma-0 pr-0 py-0 justify-end">
                    <v-container fluid v-for="audio in rightAudioData" :key="audio.audioDataId" class="pt-0 px-0 pb-5">
                        <AudioElement
                            v-if="audio.audioData.audio.length > 0 && !audioLoading"
                            :audioObject="audio"
                            :postAudioDataId="postData.audioDataId"
                            :postOwner="postData.userId"
                            :userData="userData"
                            :selectedAudioId="selectedAudioId"
                            :isGroupAdmin="isGroupAdmin"
                            :postType="postType"
                            :postData="postData"
                            :videoLoading="false"
                            :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                            :anonymousMode="postData?.anonymousMode || false"
                            :isTextPost="postData.mediaType === 'text' ? true : false"
                            @delete-selected-audio-element="deleteDialog"
                            @audioChanged="changeAudioElement"
                            @changeAudioSegment="changeAudioSegment"
                            @edit-caption="editCaption"
                            @saveEditAudioElementTitle="saveEditAudioElementTitle"
                            @update-specific-audio-data="getSpecificAudioData"
                            @update-selected-subtitle-language="updateSelectedSubtitleLanguage"
                        />
                    </v-container>
                </v-col>

                <v-col v-if="audioLoading" cols="12" class="hidden-md-only hidden-sm-only">
                    <div v-for="i in 5" :key="i" class="my-0 pt-0 px-0 pb-5">
                        <v-skeleton-loader height="100" type="image" class="rounded-lg"></v-skeleton-loader>
                    </div>
                </v-col>

                <v-col v-for="audio in languageFilteredAudio" :key="audio.audioDataId" cols="12" class="hidden-md-only hidden-sm-only my-0 pt-0 px-0 pb-5">
                    <AudioElement
                        v-if="audio.audioData.text.length > 0 && !audioLoading"
                        :audioObject="audio"
                        :postAudioDataId="postData.audioDataId"
                        :postOwner="postData.userId"
                        :userData="userData"
                        :selectedAudioId="selectedAudioId"
                        :isGroupAdmin="isGroupAdmin"
                        :postType="postType"
                        :postData="postData"
                        :videoLoading="false"
                        :audioElementHasBegunPlaying="audioElementHasBegunPlaying"
                        :anonymousMode="postData?.anonymousMode || false"
                        :isTextPost="postData.mediaType === 'text' ? true : false"
                        @delete-selected-audio-element="deleteDialog"
                        @audioChanged="changeAudioElement"
                        @changeAudioSegment="changeAudioSegment"
                        @edit-caption="editCaption"
                        @saveEditAudioElementTitle="saveEditAudioElementTitle"
                        @update-specific-audio-data="getSpecificAudioData"
                        @update-selected-subtitle-language="updateSelectedSubtitleLanguage"
                    />
                </v-col>
            </v-row>
        </v-col>
    </v-row>
</template>

<script>
// import fb from "firebase/compat/app";
import firebase from "../../firebaseConfig.js";
import Alert from "./Alert.vue";
import AudioElement from "./AudioElement.vue";
import Report from "./Report.vue";
import UploadPhotoDialog from "./UploadPhotoDialog.vue";
import ShareDialog from "./ShareDialog.vue";
import TextIndexCardContainer from "./TextPost/TextIndexCardContainer.vue"
import TextCardTranslation from "./TextPost/TextCardTranslation.vue";
import { throws } from "assert";

// import VTT from "./VttDownload.vue";

const PERMISSIONS = Object.freeze({
    NONE: 0, // NO PERMISSIONS
    ALL: 1, // Public Post, allow ALL.
    OWN_AND_MAIN: 2, // ONLY CURRENT user's contributions + MAIN one (AudioElement associated with this post)
});

export default {
    name: "view-text-post",
    metaInfo() {
        return {
            title: typeof this.postData.title != 'undefined' ? `${this.postData.title} - tawq.in` : 'View Post - tawq.in',
        };
    },
    props: ["postId", "postType"],
    components: {
        Alert,
        AudioElement,
        Report,
        UploadPhotoDialog,
        ShareDialog,
        TextIndexCardContainer,
        TextCardTranslation,
        // VTT,
    },
    data: function () {
        return {
            // Dialog pop ups
            deleteSelectedAudioElementDialog: false,
            showShareDialog: false,
            reportDialog: false,
            uploadPhotoDialog: false,
            vttDialog: false,
            // END Dialog pop ups

            viewLoading: true, // set to true on July 11, 2023. viewLoading will only be true on initial load.
            audioLoading: true, // audioLoading will only be true on initial load, and will be set to false once audioData is populated.

            textAddedToDisplay: this.postData?.textAdded,
            postData: {},
            audioData: [],

            currentUserId: firebase.auth.currentUser.uid,
            letUserSeePost: false, // July 11, 2023: changed to false so that a user won't see a group post until we have confirmed they are a member of the group. This also mean a user will not see a public post until we have confirmed it is not a group post.

            menu: false, // the dots vertical (snowman) menu used to share, report, edit title/description, etc.

            audioToDelete: null,

            titleRules: [(v) => !!v || "Title is required", (v) => (v && v.length <= 50) || "Title must be 50 characters or less",],
            descriptionRules: [(v) => !!v || "Description is required", (v) => (v && v.length <= 750) || "Description must be 750 characters or less",],
            postMetadataHasChanged: false,
            postMetadataError: false,
            editPost: false,
            editPostMetadataLoading: false, // used to set editPostMetadata to a loading value so the user knows it is loading.

            previousAudioPlayer: null, // used to load the previous audio segment into the audioPlayer
            audioPlayer: new Audio(), // main audioPlayer, used to PLAY the current audio segment
            nextAudioPlayer: new Audio(), // used to load the next audio segment into the audioPlayer

            selectedAudioId: null, // used to highlight (outline) the currently playing AudioElement
            currentAudioElementIndex: null, // index of the currently selected AudioElement
            currentAudioElement: null, // the currently selected AudioElement
            previousAudioSegment: null, // the previous segment loaded into the audioPlayer
            currentAudioSegment: null, // the current segment loaded into the audioPlayer
            nextAudioSegment: null, // the next segment to be loaded into the audioPlayer

            textCardAudioIsLoading: false,
            textCardAudioIsPlaying: false,

            viewFullDescription: false,
            descriptionWordCount: 0,
            playbackRate: 1.0, // defaulted to recorded speed (normal - 1.0), until user changes it in the advanced settings of TextIndexCardContainer.vue
            captionVolume: this.$store.state.userData.captionAudio ? 1.0 : 0.0,

            filter: {
                languageSelected: "All",
                languageCodes: ["All"],
            },

            hasIncrementedPostViewed: false,
            postViewedCounter: null,
            isGroupAdmin: null,

            audioElementHasBegunPlaying: false, // used in the AudioElement component to show a replay button for the currentAudioElement
            changeAudioElementClicked: false,
            changeAudioSegmentClicked: null,

            subtitlesByLanguageSelected: false,
            selectedSubtitleLanguage: null,
            subtitleByAudioElementId: {},

            textCardTranslationDialog: false,
            showLanguageSelectionMenu: false,
            textAddedByLanguageSelected: false,
            selectedTextAddedLanguage: null,
        };
    },
    methods: {
        addNewAudio: function () {
            this.$router.push({
                name: "text-contribution",
                query: {
                    pid: this.postData.postId,
                    postType: this.postData.postType,
                },
                params: { /*postData: this.postData,*/ audioData: this.audioData, },
            });
        },
        triggerFileUpload: function() {
            if (!this.isUsersPost) {
                this.$store.commit('alertUser', { show: true, text: `You do not have permissions to upload an image for this post.`, type: 'snackbar' });
                return;
            }
            this.uploadPhotoDialog = true;
        },
        thumbnailUploadSuccess: function () {
            ((this.postType === 'group') ? this.getGroupPostData() : this.getPostData());
            this.$store.commit('alertUser', { show: true, text: `Successfully uploaded post thumbnail.`, type: 'snackbar' });
        },
        updateAnonymousMode: async function () {
            try {
                const collectionName = (this.postType === 'group' ? 'groupPosts' : 'publicPosts');
                const docRef = firebase.db.collection(collectionName).doc(this.postId);
                const newValue = (this.postData.anonymousMode ? !this.postData.anonymousMode : true);

                await docRef.update({ anonymousMode: newValue }, { merge: true });

                ((this.postType === 'group') ? this.getGroupPostData() : this.getPostData());
            } catch (error) {
                console.error("Error updating anonymousMode: ", error);
                this.$store.commit('alertUser', {show: true, text: "Something went wrong updating anonymousMode. Please try again!", type: 'snackbar',});
            }
        },
        editPostMetadata: async function () {
            if (this.postMetadataHasChanged && this.$refs.editDescription.valid && this.$refs.editTitle.valid) {
                this.editPostMetadataLoading = true;
                this.audioLoading = true;

                try {
                    await this.$http.put(`/post/metadata/${this.postType}/${this.postId}`, {
                        description: this.postData.description,
                        title: this.postData.title,
                        audioDataId: this.postData.audioDataId,
                        pinnedPost: (this.postType == 'public' && this.postData.pinnedPost) ? this.postData.pinnedPost : false,
                    });

                    this.$store.commit('alertUser', { show: true, text: `Successfully Updated Post Title and/or Description.`, type: 'snackbar' });
                    // ((this.postType === 'group') ? this.getGroupPostData() : this.getPostData());
                    this.updateLocalPostAndAudioData();

                    // this.getMainPostAudioData(); // retrieve the updated postAudioData audioSegment (the main one for the post);
                    this.editPost = false;
                } catch (error) {
                    console.error("Error updating post metadata", error.message);
                    this.$store.commit('alertUser', { show: true, text: `Error Updating Post Title and/or Description.`, type: 'snackbar' });
                    this.editPostMetadataLoading = false;
                    this.audioLoading = false;
                    this.editPost = false;
                }
            } else {
                this.editPost = false;
            }
        },
        deleteDialog: function (audioObject) { // Opens delete audio element dialog with the selected AudioElement (audioObject) to delete
            this.audioToDelete = audioObject;
            this.deleteSelectedAudioElementDialog = true;
        },
        deleteAudioElement: async function (usersChoice) { // Handle users choice from the delete audio element dialog
            this.deleteSelectedAudioElementDialog = false;
            if (!usersChoice || !this.audioToDelete) return;

            try {
                await this.deleteAudioMetadataBatch(this.audioToDelete.audioData.userId, this.audioToDelete.audioDataId); // Send audioToDelete to deleteAudioMetadataBatch to delete audio metadata in Firestore Database
                await this.deleteAudioFilesFromStoragePagination(this.postData.postId, this.audioToDelete.audioDataId); // Send audioToDelete to deleteAudioFilesFromStorage to delete audio file from Firebase Storage
                this.$store.commit('alertUser', { show: true, text: 'Successfully deleted Audio Element.', type: 'snackbar', });

                await this.getAudioData?.(); // Refresh the UI to remove the deleted AudioElement
            } catch (error) {
                console.error('Delete Audio Element failed:', error.message);
                this.$store.commit('alertUser', { show: true, text: `Error deleting Audio Element: ${error.message || error}`, type: 'snackbar', });
            } finally {
                this.audioToDelete = null;
            }
        },
        deleteAudioMetadataBatch: async function (userId, audioDataId) {
            const postCollectionType = (this.postData.postType === 'public') ? "publicPosts" : "groupPosts"
            const postRef = firebase.db.collection(postCollectionType).doc(this.postId);
            const contributionsRef = postRef.collection("contributions").doc(userId);
            // const analyticsRef = firebase.db.collection("admin").doc("analytics");
            const audioSegmentsRef = firebase.db.collection("audioSegments").doc(audioDataId);

            const FieldValue = (firebase.fb?.firestore?.FieldValue) || firebase.firestore.FieldValue;

            const batch = firebase.db.batch();

            batch.update(contributionsRef, { audioDataId: FieldValue.arrayRemove(audioDataId) }); // Remove the audioDataId from the post's contributions collection via the user who created it
            // batch.update(postRef, { "postAnalytics.total_contributions": FieldValue.increment(-1) }); // Decrement the total number of contributions for the post // This was commented out by Scott Brady on September 11, 2025 because we don't have postAnalytics for Text/Dictionary Posts.
            // batch.update(analyticsRef, { total_contributions: FieldValue.increment(-1) }); // Decrement the total number of contributions for all posts on the site // This was commented out by Scott Brady on September 11, 2025 because we don't have postAnalytics for Text/Dictionary Posts.
            batch.delete(audioSegmentsRef); // Delete the audioSegments document, which contains the metadata for the AudioElement we are deleting

            await batch.commit();
        },
        deleteAudioFilesFromStoragePagination: async function (postId, audioDataId, batchSize = 200) {
            const audioStorageRef = firebase.storage.ref(`audioSegments/${postId}/${audioDataId}`);

            const deleteWithRetry = async (itemRef, attempts = 3) => { // Retry helper (handles transient errors)
                let lastErr;
                for (let i = 0; i < attempts; i++) {
                    try { await itemRef.delete(); return; }
                    catch (err) { lastErr = err; await new Promise(r => setTimeout(r, 150 * (i + 1))); } // small backoff before retry
                }
                console.error(`Failed to delete ${itemRef.fullPath}:`, lastErr);
            };

            let pageToken = undefined;
            do {
                const page = await audioStorageRef.list({
                    maxResults: batchSize,
                    pageToken,
                });

                /* retrieve the file names of each segment, so that we can delete each file
                individually from the storage bucket. GCS doesn't allow us to delete the full bucket */
                await Promise.all(page.items.map(itemRef => deleteWithRetry(itemRef))); // Delete files in this page in parallel

                pageToken = page.nextPageToken;
            } while (pageToken); // Ends when there are no more pages (items in list) to delete
        },
        saveEditAudioElementTitle: async function (audioObject) { // This function is used to update the title of the AudioElement, when it's not the main post audio
            let audioSegmentsRef = firebase.db.collection("audioSegments").doc(audioObject.audioObject.audioDataId);
            await audioSegmentsRef.update({title: audioObject.audioObject.audioData.title}, {merge: true});
        },
        editCaption: async function (caption) {
            try {
                const audioSegmentsRef = firebase.db.collection('audioSegments').doc(caption.audioSegId);

                await firebase.db.runTransaction(async (tx) => {
                    const snap = await tx.get(audioSegmentsRef);
                    if (!snap.exists) throw new Error('audioSegments document not found');

                    const isTextPost = this.postData.mediaType === 'text';
                    const fieldName = isTextPost ? 'text' : 'subtitles';
                    const matchKey = isTextPost ? 'segment' : 'audioSegId';
                    const matchValue = isTextPost ? caption.caption.segment : caption.caption.audioSegId;

                    // The following should/can be used if we are updating more than just the subtitle, instead of 'snap.data()[fieldName]' we can use just 'snap.data()',
                    // and change the matchKey and matchValue variables. We might have to change a few other things but the below way is more efficient when updating multiple fields
                    /* const dataArray = snap.data()[fieldName];
                    const updatedArray = dataArray.map(item => {
                        if (item[matchKey] === matchValue) {
                            return {
                                ...item,
                                subtitle: caption.caption.subtitle,
                                ...(this.postType === 'group' && { audioSegFeedback: caption.caption.audioSegFeedback })
                            };
                        }
                        return item;
                    }); */

                    const dataArray = snap.get(fieldName) || []; // Safely get the array containing the captions/subtitles
                    if (!Array.isArray(dataArray)) throw new Error(`${fieldName} field is not an array`);

                    const idx = dataArray.findIndex(item => item && item[matchKey] === matchValue); // Find the item to update
                    if (idx === -1) throw new Error(`Target ${fieldName} item not found`);

                    const existing = dataArray[idx] || {};
                    const next = {
                        ...existing,
                        subtitle: caption.caption.subtitle,
                        ...(this.postType === 'group' && { audioSegFeedback: caption.caption.audioSegFeedback })
                    };

                    const sameSubtitle = existing.subtitle === next.subtitle; // Check if the caption/subtitle has changed
                    // const sameFeedback = JSON.stringify(existing.audioSegFeedback || null) === JSON.stringify(next.audioSegFeedback || null);

                    if (sameSubtitle /*&& sameFeedback*/) return; // Avoid write if nothing changed

                    const updatedArray = dataArray.slice();
                    updatedArray[idx] = next;

                    tx.update(audioSegmentsRef, { [fieldName]: updatedArray });
                });

                this.$store.commit('alertUser', { show: true, text: 'Caption successfully updated!', type: 'snackbar' }); // Show user a snackbar to indicate success

            } catch (err) {
                console.error('editCaption failed:', err);
                this.$store?.commit?.('alertUser', { show: true, text: `Error updating caption: ${err.message || err}`, type: 'snackbar' });
            }
        },
        closeTextCardTranslationDialog: function(wasTranslated) {
            if (wasTranslated) this.getMainPostData(); // refresh the main post data to get the newly translated text data if a translation was made
            this.textCardTranslationDialog = false;
        },
        setSelectedTextAddedLanguage: function(language) {
            this.textAddedByLanguageSelected = true;
            this.selectedTextAddedLanguage = language;//.split('-')[0].trim();
            if (this.selectedTextAddedLanguage === this.textPost.textAddedLanguage.value + " - " + this.textPost.textAddedLanguage.text) this.textAddedByLanguageSelected = false;
            this.updateSelectedTextAddedLanguage(this.selectedTextAddedLanguage, this.textAddedByLanguageSelected);
        },
        updateSelectedTextAddedLanguage: function (language, textAddedByLanguageSelected) {
            console.log("Selected text added language:", language, "Text added by language selected:", textAddedByLanguageSelected);
        },
        setSelectedSubtitleLanguage: function(language) {
            this.subtitlesByLanguageSelected = true;
            this.selectedSubtitleLanguage = language;//.split('-')[0].trim();
            if (this.selectedSubtitleLanguage === this.currentAudioElement.audioData.languageName) this.subtitlesByLanguageSelected = false;
            this.updateSelectedSubtitleLanguage(this.selectedSubtitleLanguage, this.subtitlesByLanguageSelected, this.currentAudioElement);
        },
        updateSelectedSubtitleLanguage: function (language, subtitlesByLanguageSelected, audioObject) {
            this.$set(this.subtitleByAudioElementId, audioObject.audioDataId, {
                language, subtitlesByLanguageSelected,
            });

            if (audioObject.audioDataId !== this.currentAudioElement.audioDataId) return;

            this.$refs.vidPlayer.hasTextTrack() && this.$refs.vidPlayer.removeCue(true); // IMPORTANT: When switching subtitles mid-play, re-sync cues immediately

            this.subtitlesByLanguageSelected = subtitlesByLanguageSelected;
            this.selectedSubtitleLanguage = language;

            let idx = this.currentAudioSegment.index;

            if ((idx - 1) >= 0) this.previousAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[idx - 1], idx - 1);
            this.currentAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[idx], idx);
            if ((idx + 1) < this.currentAudioElement.audioData.audio.length) this.nextAudioSegment = this.applySegmentMetadata(this.currentAudioElement.audioData.audio[idx + 1], idx + 1);

            // Optional but recommended: pause audio so your timeUpdate guards don't block re-arming
            // if (!this.audioPlayer.paused) {
            //     this.audioPlayer.pause();
            //     // this.audioEnded(); // if this is how you advance segment indices / state
            //     // Force a sync using the *current* video time
            //     const now = this.$refs.vidPlayer.playerGetTime?.() ?? 0;
            //     this.timeUpdate(Number(now.toFixed(1)));
            // }
        },
        applySegmentMetadata: function (segment, index) {
            if (!segment) return segment;
            segment.index = index;
            const subtitle = this.subtitlesByLanguageSelected ? (this.currentAudioElement?.audioData?.subtitlesByLanguage[this.selectedSubtitleLanguage]?.[index]) : (this.currentAudioElement?.audioData?.subtitles?.[index]);
            if (subtitle) {
                segment.subtitle = subtitle.subtitle;
                segment.subtitleSegment = subtitle.audioSegId;
            }
            return segment;
        },
        loadAndPreloadFirstAudioData: async function () {
            // This function is called on initial load of the page and anytime a new AudioElement is clicked.
            // It will load the first available (with an audioURL) AudioSegment into the audioPlayer and preload the next available (with an audioURL) AudioSegment.

            this.textCardAudioIsLoading = true;

            this.currentAudioSegment = this.currentAudioElement.audioData.text[0]; // load first AudioElements text data into this.currentAudioSegment
            this.currentAudioSegment.index = 0;
            this.currentAudioSegment.hasBeenPlayed = false; // set this to false so it will show a play button for the currently selected text card
            this.addAudioPlayerEventListeners(); // Add event listeners to this.audioPlayer here so that we don't miss any events the browser might fire when setting this.audioPlayer below

            this.audioPlayer.preload = 'auto'; // preload the main (current) audioPlayer so we don't miss any events (browser can fetch aggressively)
            this.audioPlayer.src = this.currentAudioSegment.audioURL; // set the first AudioSegment audio URL into this.audioPlayer src
            this.audioPlayer.load(); // load the audio data so we can play the audio file

            if (this.currentAudioElement.audioData.text.length > 1) { // preloadNextAudioSegment, if available, so it is ready for play when it's text card is selected
                this.nextAudioSegment = this.currentAudioElement.audioData.text[1];
                this.nextAudioSegment.index = 1;
                this.nextAudioSegment.hasBeenPlayed = false;

                if (this.nextAudioSegment != null) {
                    this.nextAudioPlayer = new Audio();
                    this.nextAudioPlayer.src = this.nextAudioSegment.audioURL;
                    this.nextAudioPlayer.load();
                }
            } else { // only one AudioSegment to play. No need for nextAudioPlayer. Destroying nextAudioPlayer.
                this.nextAudioSegment = null;
                this.nextAudioPlayer = this.nextAudioSegment;
            }
        },
        loadAndPreloadNextAudioData: async function (currentTextAudioSegment) {
            if (!currentTextAudioSegment || currentTextAudioSegment.segment == this.currentAudioSegment.segment) return;

            this.previousAudioSegment = this.currentAudioSegment;
            this.currentAudioSegment = this.nextAudioSegment;

            this.previousAudioPlayer = this.audioPlayer; // set this.previousAudioPlayer = this.audioPlayer, so it will stay loaded and be ready to go when needed.
            this.audioPlayer = this.nextAudioPlayer; // set this.audioPlayer = this.nextAudioPlayer, since it is already loaded it should be ready to go when set.
            this.nextAudioPlayer = null;
            this.addAudioPlayerEventListeners();

            var nextAvailableIndex;
            if (this.nextAudioSegment != null && ((this.currentAudioElement.audioData.text.length-1) >= (this.nextAudioSegment.index+1))) {
                // preload next available AudioSegment so it is ready for play when it's reached.
                nextAvailableIndex = this.nextAudioSegment.index+1; // Check to see if index+1 is out of range, if so set this.nextAudioSegment to null
                this.nextAudioSegment = this.currentAudioElement.audioData.text[nextAvailableIndex];
                this.nextAudioSegment.subtitleSegment = this.currentAudioElement.audioData.text[nextAvailableIndex].segment;
                this.nextAudioSegment.index = nextAvailableIndex;

                this.nextAudioPlayer = new Audio();
                this.nextAudioPlayer.preload = 'auto';
                this.nextAudioPlayer.src = this.nextAudioSegment.audioURL;
                this.nextAudioPlayer.load();
            } else { // only one AudioSegment to play. No need for nextAudioPlayer. Destroying nextAudioPlayer.
                this.nextAudioSegment = null;
            }
        },
        loadAndPreloadPreviousAudioData: async function (index) {
            this.nextAudioSegment = this.currentAudioSegment;
            this.currentAudioSegment = this.previousAudioSegment;

            this.nextAudioPlayer = this.audioPlayer;
            this.nextAudioPlayer.load();

            this.audioPlayer.preload = "auto";
            this.audioPlayer = this.previousAudioPlayer;
            this.audioPlayer.load();
            this.addAudioPlayerEventListeners();

            this.previousAudioPlayer = null;

            if (index > 0 && (this.currentAudioElement.audioData.text[index - 1] != undefined)) {
                this.previousAudioSegment = this.currentAudioElement.audioData.text[index - 1];
                this.previousAudioSegment.index = index - 1;

                this.previousAudioPlayer = new Audio();
                this.previousAudioPlayer.src = this.previousAudioSegment.audioURL;
                this.previousAudioPlayer.load();
            } else {
                this.previousAudioSegment = null;
            }
        },
        loadAndPreloadAudioDataAfterClick: async function (index) {
            if (index == null) return;

            this.textCardAudioIsLoading = true;

            this.previousAudioPlayer = null;
            this.audioPlayer = null;
            this.nextAudioPlayer = null;

            if (index > 0 && (this.currentAudioElement.audioData.text[index - 1] != undefined)) {
                this.previousAudioSegment = this.currentAudioElement.audioData.text[index - 1];
                this.previousAudioSegment.index = index - 1;

                this.previousAudioPlayer = new Audio();
                this.previousAudioPlayer.preload = 'auto';
                this.previousAudioPlayer.src = this.previousAudioSegment.audioURL;
                this.previousAudioPlayer.load();
            } else {
                this.previousAudioSegment = null;
            }

            this.currentAudioSegment = this.currentAudioElement.audioData.text[index];
            this.currentAudioSegment.index = index;

            this.audioPlayer = new Audio();
            this.audioPlayer.preload = 'auto';
            this.audioPlayer.src = this.currentAudioSegment.audioURL;
            this.audioPlayer.load();
            this.addAudioPlayerEventListeners();

            if ((this.currentAudioElement.audioData.text.length-1) >= (index+1)) {
                // preload next available AudioSegment so it is ready for play when it's reached.
                this.nextAudioSegment = this.currentAudioElement.audioData.text[(index+1)];
                this.nextAudioSegment.index = (index+1);

                this.nextAudioPlayer = new Audio();
                this.nextAudioPlayer.preload = 'auto';
                this.nextAudioPlayer.src = (this.nextAudioSegment && this.nextAudioSegment.audioURL);
                this.nextAudioPlayer.load();
            } else { // only one AudioSegment to play. No need for nextAudioPlayer. Destroying nextAudioPlayer.
                this.nextAudioSegment = null;
            }
        },
        loadAndPreloadAudioData: function (loadAndPreloadValue, currentTextAudioSegment, textCardIndexValue) {// 0 = previous, 1 = next, 2 = after click
            if (loadAndPreloadValue == 0) { // Previous -- user went backwards by clicking left of card or left arrow chevron
                if (currentTextAudioSegment && this.previousAudioSegment && (currentTextAudioSegment.index == this.previousAudioSegment.index)) {
                    this.loadAndPreloadPreviousAudioData(currentTextAudioSegment.index);
                } else { // user went backwards by double clicking left arrow chevron, or by using the text card menu and skipping more than one card
                    this.loadAndPreloadAudioDataAfterClick((currentTextAudioSegment ? (currentTextAudioSegment.index != undefined ? currentTextAudioSegment.index : (currentTextAudioSegment.segment-1)) : null));
                }
            } else if (loadAndPreloadValue == 1) { // Next -- user went forwards by clicking right of card or right arrow chevron, ONE CLICK ONLY!
                this.loadAndPreloadNextAudioData(currentTextAudioSegment);
            } else/* if (loadAndPreloadValue == 2)*/ { // After click -- user went forwards by double clicking right arrow chevron or by selecting a higher numbered card from the text card menu
                this.loadAndPreloadAudioDataAfterClick((currentTextAudioSegment ? (currentTextAudioSegment.index != undefined ? currentTextAudioSegment.index : textCardIndexValue) : null));
            }
        },
        addAudioPlayerEventListeners: function () {
            this.audioPlayer.addEventListener('canplaythrough', this.audioCanPlayThrough/*, {once: true}*/);
            this.audioPlayer.addEventListener("ended", this.audioEnded/*, {once: true}*/);
        },
        removeAudioPlayerEventListeners: function () {
            this.audioPlayer.removeEventListener("canplaythrough", this.audioCanPlayThrough);
            this.audioPlayer.removeEventListener("ended", this.audioEnded/*, {once: true}*/);
        },
        audioCanPlayThrough: function () {
            this.textCardAudioIsLoading = false;
        },
        audioEnded: function (/*event*/) {
            this.textCardAudioIsPlaying = false;
            this.currentAudioSegment.hasBeenPlayed = true;
            this.$refs.textIndexCardContainer?.handleAutoSkipTrigger();
        },
        adjustCaptionVolume: function (value) {
            this.audioPlayer.volume = value;
        },
        adjustPlaybackRate: function (rate) {
            this.playbackRate = rate;
            this.audioPlayer.playbackRate = rate;
        },
        playOrPauseAudio: function (resetHasBeenPlayed) {
            this.audioElementHasBegunPlaying = true; // used in the AudioElement component to show a replay button for the currentAudioElement
            this.textCardAudioIsPlaying = !this.textCardAudioIsPlaying;
            if (!this.textCardAudioIsPlaying) { // PAUSE AUDIO
                this.audioPlayer.pause();
            } else { // PLAY AUDIO
                if (resetHasBeenPlayed) this.currentAudioSegment.hasBeenPlayed = false;
                this.audioPlayer.playbackRate = this.playbackRate;
                this.audioPlayer.play();
            }
        },
        changeAudioSegment: function (data) { // this function is ran only when a audioSegment is clicked. It will update the markers if necessary and playSelectedAudioSegment

            !this.audioPlayer.paused && this.audioPlayer.pause(); // if the player is NOT paused then pause, else nothing;
            this.textCardAudioIsPlaying = false;
            this.removeAudioPlayerEventListeners();

            if (this.selectedAudioId === data.audioId) { // If the AudioSegment clicked is the same as the current AudioElement
                if (data.audioSeg.audioSeg.textCardIndexAssociationValue === this.$refs?.textIndexCardContainer?.currentSubtitle?.textCardIndexAssociationValue) {
                    return;
                } else {
                    this.changeAudioSegmentClicked = data.audioSeg.audioSeg;
                }
            } else {
                this.currentAudioElement = {
                    audioDataId: data.audioId,
                    audioData: data.audioData,
                };
                this.selectedAudioId = data.audioId;

                data.audioSeg.audioSeg.index = data.audioSeg.index;
                data.audioSeg.audioSeg.hasBeenPlayed = false;

                this.changeAudioSegmentClicked = data.audioSeg.audioSeg;

                this.$router.replace({path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId}});

                this.audioElementHasBegunPlaying = false; // Do I need this???
            }
            // this.loadAndPreloadAudioDataAfterClick(data.audioSeg.index);
            // this.audioPlayer = new Audio();
            this.loadAndPreloadAudioData(2, data.audioSeg.audioSeg, data.audioSeg.index);
        },
        changeAudioElement: function (audioData, audioId) { // This function is called Everytime the an AudioElement is clicked.

            if (this.selectedAudioId === audioId) return; // The currently selected AudioElement is clicked again, return
            this.changeAudioElementClicked = true;

            !this.audioPlayer.paused && this.audioPlayer.pause();
            this.textCardAudioIsPlaying = false;
            this.removeAudioPlayerEventListeners();

            this.currentAudioElement = {
                audioDataId: audioId,
                audioData: audioData,
            };
            this.selectedAudioId = audioId;
            this.$router.replace({path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId}});

            this.audioElementHasBegunPlaying = false;
            this.loadAndPreloadFirstAudioData();
        },
        getMainPostData: async function () {
            // console.log("Refresh the Text Post Data by calling getMainPostData");
            const doc = await firebase.db.collection((this.postData.postType === "group") ? "groupPosts" : "publicPosts").doc(this.postData.postId).get();
            if (!doc.exists) return;

            this.postData = doc.data();
            this.$store.commit('updatePostData', this.postData); // this updates the postData in the store so that the homepage, and other pages, can get the changes aswell.
        },
        updateLocalPostAndAudioData: function () {
            this.audioLoading = true;
            this.editPost = false;
            this.postMetadataHasChanged = false;
            this.editPostMetadataLoading = false;

            this.audioData[0].audioData.title = this.postData.title; // only the title needs to be updated on the AudioElement
            this.audioLoading = false;
            this.$store.commit('updatePostData', this.postData); // this updates the postData in the store so that the homepage, and other pages, can get the changes aswell.
        },
        getPostData: async function () {
            try {
                if (!this.postId) throw new Error('Missing postId');

                const snap = await firebase.db.collection('publicPosts').doc(this.postId).get();
                if (!snap.exists) return this.$store.commit('alertUser', { show: true, text: 'This post does not exist.', type: 'snackbar' });

                const data = snap.data();

                let toDate = data.postDate.toDate();
                let day = toDate.getDate();
                let month = toDate.toLocaleDateString('default', {month: 'short'});
                let year = toDate.getFullYear();
                let formattedDate = `${day} ${month} ${year}`;

                // the following code block will check to see if a post has 'multipleLanguagesTitle' object in the database, and if so, will set the title and audioDataId to the corresponding default browser language or to default (english)
                const browserLang = (navigator?.language || navigator?.userLanguage || 'en').split('-')[0];
                const mlt = data.multipleLanguagesTitle;
                const localized = (mlt && typeof mlt === 'object') ? (mlt[browserLang] || mlt.en) : null;

                const post = { // Build the post object in one place
                    ...data,
                    ...(formattedDate ? { postDate: formattedDate } : {}),
                    ...(localized ? { title: localized.title ?? data.title, audioDataId: localized.audioId ?? data.audioDataId, } : {}),
                };

                this.postData = post; // Set state

                // Derived values / side effects
                this.descriptionWordCount = this.wordCount(post.description || '');
                this.$store.dispatch('saveCurrentPost', post);

                this.letUserSeePost = true;

                if (this.editPost) this.editPost = false;
                if (this.postMetadataHasChanged) {
                    this.$store.commit('updatePostData', post); // this updates the postData in the store so that the homepage, and other pages, can get the changes aswell.
                    this.postMetadataHasChanged = false;
                    this.editPostMetadataLoading = false;
                    await this.getMainPostAudioData();
                } else {
                    await this.getAudioData(); // keep flow consistent
                }


            } catch (err) {
                console.error('getPostData error:', err);
                this.$store.commit('alertUser', { show: true, text: `Something went wrong. ${err.message || err}`, type: 'snackbar' });
            } finally {
                this.viewLoading = false;
            }
        },
        getGroupPostData: async function () {
            try {
                if (!this.postId) throw new Error('Missing postId');

                const snap = await firebase.db.collection('groupPosts').doc(this.postId).get();
                if (!snap.exists) return this.$store.commit('alertUser', { show: true, text: 'This post does not exist.', type: 'snackbar' });

                const data = snap.data();

                let toDate = data.postDate.toDate();
                let day = toDate.getDate();
                let month = toDate.toLocaleDateString('default', {month: 'short'});
                let year = toDate.getFullYear();
                let formattedDate = `${day} ${month} ${year}`;

                const post = { // Build final post object
                    ...data,
                    ...(formattedDate ? { postDate: formattedDate } : {}),
                };

                this.postData = post; // Set state

                const canView = ((this.userData?.admin == true) || this.isUserPartOfGroup); // Permission: group member OR site admin
                if (!canView) {
                    this.postData = {};
                    return this.$store.commit('alertUser', { show: true, text: 'This post can only be seen by members of the group it belongs to.', type: 'snackbar' });
                }

                this.descriptionWordCount = this.wordCount(post.description || '');
                await this.$store.dispatch('saveCurrentPost', post);

                this.letUserSeePost = true;

                if (this.editPost) this.editPost = false;
                if (this.postMetadataHasChanged) {
                    this.$store.commit('updatePostData', post); // this updates the postData in the store so that the homepage, and other pages, can get the changes aswell.
                    this.postMetadataHasChanged = false;
                    this.editPostMetadataLoading = false;
                    await this.getMainPostAudioData();
                } else {
                    await this.getAudioData(); // Load audio after post is ready/authorized
                }

            } catch (err) {
                console.error('getGroupPostData error:', err);
                this.$store.commit('alertUser', { show: true, text: `Something went wrong. ${err.message || err}`, type: 'snackbar' });
            } finally {
                this.viewLoading = false;
            }
        },
        setAudioPermissions: function () { // permissions: 0 = NONE, 1 = ALL,  2 = ONLY CURRENT users and MAIN one (AudioElement associated with this post)
            const isSiteAdmin = !!this.userData?.admin;
            if (isSiteAdmin) return PERMISSIONS.ALL; // If user is site admin, allow all immediately.

            if (this.postData?.postType !== 'group') return PERMISSIONS.ALL; // Public Post → everyone can see all

            // --- For Group Posts --- //
            let membership = this.userGroups?.find?.(g => g.groupId === this.postData?.groupId); // Find the user's membership in this group (if any)

            if (!membership) { // Not a member → no access
                this.letUserSeePost = false;
                this.$store.commit('alertUser', { show: true, text: `This post can only be seen members of the group it belongs to.`, type: 'snackbar' });
                return PERMISSIONS.NONE;
            }
            this.isGroupAdmin = !!membership.admin;
            if (this.isGroupAdmin) return PERMISSIONS.ALL; // Group admin → all access

            // Not admin: respect group's privacy setting
            const privateContributions = !!membership.privateContributions;
            return privateContributions ? PERMISSIONS.OWN_AND_MAIN : PERMISSIONS.ALL;
            // --- END For Group Posts --- //
        },
        getSpecificAudioData: async function (specificAudioDataId) {
            const doc = await firebase.db.collection("audioSegments").doc(specificAudioDataId).get();
            if (!doc.exists) return;

            const data = {
                audioData: doc.data(),
                audioDataId: doc.id,
            };

            const index = this.audioData.findIndex(audio => audio.audioDataId === specificAudioDataId);
            if (index !== -1) {
                this.audioData.splice(index, 1, data);
            } else {
                // if it wasn't in the list for some reason, add it
                this.audioData.push(data); // or unshift(data)
            }
        },
        getMainPostAudioData: async function () {
            // this method retrieves the postAudioData (the main audio attached to the post) ONLY. Currently being used after editing a post... this way we aren't retrieving ALL of them when only one has been edited...
            if (this.setAudioPermissions() === 0) return;

            const audioRef = firebase.db.collection('audioSegments').doc(this.postData.audioDataId);
            const audioDoc = await audioRef.get().catch((err) => {
                console.log("error retrieving the postAudioData document. ", err.message);
            })
            let data = {
                audioData: audioDoc.data(),
                audioDataId: audioDoc.id,
            };
            const index = this.audioData.findIndex( audio => audio.audioDataId === this.currentPost.audioDataId);
            this.audioData.splice(index != -1 ? index : 0, 1, data);
            this.audioLoading = false;
        },
        
        getAudioData: async function () {
            // This function will retrieve the metadata for the AudioElements and subsequent AudioSegments
            // We will query the returned data, adding them to our local array, the main AudioElement first.

            if (!this.hasIncrementedPostViewed) {
                clearTimeout(this.postViewedCounter);
                this.postViewedCounter = setTimeout(() => { // July 13, 2023: the following incrementPostViewed if statement was moved here in hopes that it will get called everytime and not just in the $nextTick function of setPost();
                    this.incrementPostViewed(); // this only increments when on production. I.E. tawq.in NOT localhost or developer.tawq.in;
                }, 15000); // increment the posts viewed by one after 15 seconds.
            }

            let permissions = this.setAudioPermissions();
            if (permissions === 0) return;

            this.audioData = [];
            this.currentAudioElementIndex = null;
            try {
                const postId = this.postData.postId;
                const mainId = this.postData.audioDataId;

                const snap = await firebase.db.collection('audioSegments').where('postId', '==', postId).orderBy('date', 'desc').get();

                const list = [];
                let main = null;

                snap.forEach(doc => {
                    const audioData = doc.data();
                    if (!audioData || !Array.isArray(audioData.audio) || audioData.audio.length === 0) return;

                    if (permissions === 1 || (permissions === 2 && (doc.id === mainId || audioData.userId === this.currentUserId))) { // permissions: 1 = all, 2 = only current user + main
                        const item = { audioData, audioDataId: doc.id };

                        if (doc.id === mainId) main = item;
                        else list.push(item);

                        const lang = audioData.languageName;
                        if (lang && !this.filter.languageCodes.includes(lang)) {
                            this.filter.languageCodes.push(lang); // build unique language list
                        }
                    }
                });

                this.audioData = (main ? [main, ...list] : list); // Put main AudioElement first in the list

                if (!this.$route.query.aid) {
                    this.selectedAudioId = this.audioData[0]?.audioDataId || null; // no aid in url, set first AudioElements id (main audio) as selected.
                    this.currentAudioElementIndex = 0;
                    this.$router.replace({ path: this.$route.name, query: { pid: this.postData.postId, postType: this.postData.postType, aid: this.selectedAudioId }, });
                } else {
                    this.selectedAudioId = this.$route.query.aid; // aid in url, set as selected AudioElements id so it will be highlighted and play when user clicks play button
                }

                this.currentAudioElementIndex = (!this.currentAudioElementIndex) && this.audioData.findIndex(d => d.audioDataId === this.selectedAudioId);
                this.currentAudioElement = this.currentAudioElementIndex !== -1 ? this.audioData[this.currentAudioElementIndex] : null;

                this.loadAndPreloadFirstAudioData(); // Kick off your audioPlayer load and preload
            } catch (err) {
                console.error(err?.message || err);
                this.$store.commit('alertUser', { show: true, text: `Something went wrong. ${err?.message || err}`, type: 'snackbar', });
            } finally {
                this.audioLoading = false;
            }
        },
        incrementPostViewed: async function() {
            if (process.env.NODE_ENV !== 'production' || this.hasIncrementedPostViewed) return; // Only in production and only once per view

            // Prefer values from postData, fall back to props/state
            const postId = this.postData?.postId ?? this.postId;
            const postType = this.postData?.postType ?? this.postType;
            if (!postId || !postType) return;

            const collection = postType === 'public' ? "publicPosts" : "groupPosts";

            try {
                await firebase.db.collection(collection).doc(postId).update({postViewed: firebase.db.FieldValue.increment(1),});

                this.hasIncrementedPostViewed = true; // Only increment once per view, value used just in case getAudioData() is called multiple times
            } catch (err) {
                // If the field doesn't exist yet, update() still works; this just catches network/perm issues
                console.error('Error incrementing postViewed:', err?.message || err);
            }
        },
        wordCount: function () {
            var temp = this.postData.description;
            temp = temp.replace(/(^\s*)|(\s*$)/gi,"");
            temp = temp.replace(/[ ]{2,}/gi," ");
            temp = temp.replace(/\n /,"\n");
            return temp.split(' ').length;
        },
        visibilityChanged: function () {
            if (document.hidden) {
                !this.audioPlayer.paused && this.audioPlayer.pause();
                this.textCardAudioIsPlaying && (this.textCardAudioIsPlaying = !this.textCardAudioIsPlaying);
            }
        },
        setPost (timesCalled) {
            // setPost() looks to see if we can use the current post from the store. if we can, then we don't have to fetch it from firebase. If we try through 5 ticks, with no success, then we will retrieve the post from firebase.
            // we have to wrap the code in the processNextTick() function because when viewPost is created, the store variable hasn't been set
            if (timesCalled > 5) { // base case: if we have tried this 5 times already, we fallback to getting the post info from firebase;
                this.postType === 'group' ? this.getGroupPostData() : this.getPostData();
                return;
            } else {
                this.$nextTick(() => {
                    if (this.currentPost.postId === this.postId) {
                        this.postData = { ...this.currentPost };
                        // console.log("postData: ", this.postData);
                        if (this.postType === 'group' && !this.isUserPartOfGroup && !this.userData.admin) {
                            this.$store.commit('alertUser', { show: true, text: `This post can only be seen members of the group it belongs to.`, type: 'snackbar' });
                            this.viewLoading = false;
                            return;
                        }
                        this.descriptionWordCount = this.wordCount(this.postData.description);
                        this.letUserSeePost = true;
                        this.viewLoading = false;
                        this.getAudioData();
                    } else {
                        this.setPost(timesCalled + 1); // recursively call this function to see if the store has updated
                    }
                });
            }
        },
    },
    computed: {
        isMobile(){
            return this.$store.state.isMobileDevice;
        },
        isIOS(){
            return this.$store.state.isIOSDevice;
        },
        isUsersPost: function() {
            return this.postData.userId == this.currentUserId; // firebase.auth.currentUser.uid;
        },
        isUserPartOfGroup: function() {
            if (!this.userGroups?.length) return false;
            return this.userGroups.some(group => group.groupId === this.postData.groupId);
        },
        condensed() {
            return this.postData.description.substring(0,150);
        },
        getMaxHeightMainContainer() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '60vh'; // Max height for small and down screens (sm/xs)
            } else {
                return '80vh'; // Max height for medium and up screen (md/lg/xl)
            }
        },
        getMaxHeightSubContainer() {
            if (this.$vuetify.breakpoint.smAndDown) {
                return '50vh'; // Max height for small and down screens (sm/xs)
            } else {
                return '60vh'; // Max height for medium and up screen (md/lg/xl)
            }
        },
        languageFilteredAudio(){
            if (this.filter.languageSelected === "All") {
                return this.audioData;
            } else {
                return this.audioData.filter( audio => audio.audioData.languageName === this.filter.languageSelected );
            }
        },
        leftAudioData(){
            if (this.$vuetify.breakpoint.md || this.$vuetify.breakpoint.sm) {
                if (this.filter.languageSelected === "All") {
                    return this.audioData.filter((num, index) => index%2 === 0);
                } else {
                    return this.audioData.filter((audio,index) => audio.audioData.languageName === this.filter.languageSelected && index%2 === 0);
                }
            } else {
                return [];
            }
        },
        rightAudioData(){
            if (this.$vuetify.breakpoint.md || this.$vuetify.breakpoint.sm) {
                if (this.filter.languageSelected === "All") {
                    return this.audioData.filter((num, index) => index%2 != 0);
                } else {
                    return this.audioData.filter( (audio,index) => audio.audioData.languageName === this.filter.languageSelected && index%2 != 0);
                }
            } else {
                return [];
            }
        },
        userData(){
            return this.$store.state.userData;
        },
        userGroups(){
            return this.$store.state.userGroups;
        },
        userGroupLength(){
            return this.$store.getters.getUserGroupsLength
        },
        currentPost(){
            return this.$store.state.currentPost;
        },
        displayLang() {
            const langMap = {
                en: 'English',
                es: 'Spanish',
                ar: 'Arabic',
                // Add more language mappings as needed
            };

            return langMap[this.currentAudioElement.audioData.languageCode.slice(0,2)] || this.currentAudioElement.audioData.languageCode.slice(0,2);
        },
    },
    beforeDestroy: function () {
        document.removeEventListener("visibilitychange", this.visibilityChanged);
        clearTimeout(this.postViewedCounter); // if they haven't stayed on the page for 15 seconds the timeout will stay active unless being destroyed
        this.audioPlayer.removeEventListener("ended", this.audioEnded);
        this.audioPlayer.removeEventListener("canplaythrough", this.audioCanPlayThrough);
    },
    created: function () {
        this.setPost(1);
    },
    mounted: function () {
        document.addEventListener("visibilitychange", this.visibilityChanged); // if the tab isn't being viewed we will pause the video
        // if (this.postId === "rXm4nZfBwDP9DIeycLzS") { // this if statement is used to set the below values of the pinned post 'how to use tawq.in'
        //     this.captionVolume = 1.0;
        // }
        this.audioPlayer.volume = this.captionVolume;
    },
  
};
</script>

<style scoped>
@import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

.dialog-bottom-transition-enter-active,
.dialog-bottom-transition-leave-active {
    transition: opacity 0.5s, transform 0.5s;
}
.dialog-bottom-transition-enter {
    opacity: 0;
    transform: translateY(100%);
}
.dialog-bottom-transition-leave-to {
    opacity: 0;
    transform: translateY(100%);
}

.v-input >>> .v-input__prepend-outer {
    margin-right: 0 !important;
}

.v-input >>> .v-input__append-outer {
    margin-left: 0 !important;
}

.expand {
    min-height: 105px !important;
    max-height: 600px !important;
    padding-bottom: 1em;
    /* overflow-y: scroll !important; */
}

.faded {
    position: absolute; 
    bottom: 1em;
    /* width: 100%; */
    width: 92%;
    height: 2em;
    background-image: linear-gradient(to bottom, rgba(255,255,255, 0) 0%, rgba(255,255,255, 1) 75%);
}

.faded-dark {
    position: absolute; 
    bottom: 1em;
    width: 90%;
    height: 2em;
    background-image: linear-gradient(to bottom, rgba(0,0,0, 0) 0%,rgba(18,18,18, 1) 75%);
    transition: display .4s;
}

.bordered {
    background-color: var(--v-background-base);
    /* border: 5px solid var(--v-background-base); */
    border-radius: 25px;
}
.outlined {
    background-color: var(--v-background-base);
    border: 2px solid grey;
}

.v-skeleton-loader__image {
    height: 100%;
}
</style>